import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { DRStatusBadge } from "../components/etams/StatusBadge";
import { DR_STATUS_LABEL } from "../lib/statusLabels";
import { labelPaket, labelProduk } from "../lib/faKonfigurasi";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

// Permintaan Desain — LAYAR ACUAN pola daftar seragam, mode SERVER penuh
// (27 Sep 2026; `docs/POLA_DAFTAR.md`). Salin bentuk layar ini, bukan layar lama.
//
// ⛔ DULU layar ini memindai sampai 5.000 PD lalu menyaring di browser, ditambah
//    puluhan panggilan nama customer terpisah. Uji beban (3 tahun, 2 region):
//    5,3 detik dan 6,5 MB untuk Owner — dan TETAP terpotong di 6.480 PD, jadi
//    "tidak ketemu" bisa berarti "di luar yang sempat dipindai". Sekarang satu
//    panggilan per halaman: cari (nomor PD / nama / telepon customer), urut,
//    saringan status, dan total dikerjakan server; nama customer ikut di baris
//    (`customer_name`) HANYA untuk peran yang berhak membaca customer, di cakupannya —
//    desainer/SPV tetap melihat "—", persis seperti sebelumnya.
//    K206 butir 6 (12 Okt 2026): Admin Produksi juga tidak menerima nama customer; ia menerima
//    NAMA TIM (`nama_team`, dari FA hasil promosi) — diputuskan backend, layar hanya menampilkan.

//: Cermin `URUT_SAH_DR` di backend/routers/design_requests.py.
const URUT_SAH = ["created_at", "dr_number", "dr_status", "package_type", "product_summary_type"];

const KOLOM_CSV = [
  { kunci: "dr_number", judul: "Nomor PD" },
  { kunci: "customer_name", judul: "Customer", ambil: (d) => d.customer_name || "" },
  { kunci: "nama_team", judul: "Tim", ambil: (d) => d.nama_team || "" },
  { kunci: "product_summary_type", judul: "Tipe", ambil: (d) => labelProduk(d.product_summary_type) },
  { kunci: "qty", judul: "Qty", ambil: (d) => `${d.preliminary_qty_atasan || 0}${d.preliminary_qty_celana > 0 ? ` + ${d.preliminary_qty_celana}` : ""}` },
  { kunci: "package_type", judul: "Paket", ambil: (d) => labelPaket(d.package_type) },
  { kunci: "dr_status", judul: "Status", ambil: (d) => DR_STATUS_LABEL[d.dr_status] || d.dr_status },
];

export default function DesignRequests() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      status: {
        label: "Status",
        param: "status_eq",
        tampil: (v) => DR_STATUS_LABEL[v] || v,
      },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const bangunParams = useCallback((ovr = {}) => ({
    ...paramSaringan,
    ...(cari && { q: cari }),
    limit, offset, sort, arah, ...ovr,
  }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/design-requests", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/design-requests", { params: bangunParams() });
      setItems(r.data?.items || []);
      setTotal(typeof r.data?.total === "number" ? r.data.total : (r.data?.items || []).length);
    } catch (e) {
      setErr(apiErrorMessage(e, "Gagal memuat daftar PD."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  const kataCari = daftar.cari.trim();

  return (
    <div data-testid="dr-list-page">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="text-2xl font-bold">Permintaan Desain</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="dr-reload">Muat ulang</Button>
          <Button data-testid="dr-new-btn" onClick={() => navigate("/design-requests/new")}>
            + PD Baru
          </Button>
        </div>
      </div>
      <PalangDaftar daftar={daftar} placeholder="Cari nomor PD / nama / telepon customer" testId="dr-palang"
                    ekspor={{ nama: "permintaan_desain", kolom: KOLOM_CSV, ambilHalaman }}>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={daftar.nilaiSaringan.status}
          onChange={(e) => daftar.setSaringan("status", e.target.value)}
          aria-label="Saring status"
          data-testid="dr-filter-status"
        >
          <option value="">Semua status</option>
          {Object.entries(DR_STATUS_LABEL).map(([kode, label]) => (
            <option key={kode} value={kode}>{label}</option>
          ))}
        </select>
      </PalangDaftar>
      {err && (
        <div className="bg-danger-soft border border-danger-soft-border p-3 rounded text-sm text-danger-soft-foreground mb-3">
          {/* ⚠ SENGAJA BUKAN JSON.stringify(err): objek galat mentah tidak bisa
              ditindaklanjuti CS dan bisa memuat isi permintaan. */}
          {typeof err === "string" ? err : (err?.message || "Gagal memuat daftar PD.")}
        </div>
      )}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="dr_number" className="px-4 py-2">Nomor PD</ThUrut>
                <th className="px-4 py-2">Customer</th>
                <ThUrut daftar={daftar} kunci="product_summary_type" className="px-4 py-2">Tipe</ThUrut>
                <th className="px-4 py-2">Qty</th>
                <ThUrut daftar={daftar} kunci="package_type" className="px-4 py-2">Paket</ThUrut>
                <ThUrut daftar={daftar} kunci="dr_status" className="px-4 py-2">Status</ThUrut>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-6 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-6 text-center text-muted-foreground" data-testid="dr-kosong">
                  {/* ⚠ Kalimat berbeda untuk keadaan berbeda: "pencarianmu tidak cocok"
                      tidak boleh terbaca sebagai "datanya tidak ada". Pencarian kini
                      di SERVER atas SELURUH PD — tidak ada lagi batas pindai. */}
                  {kataCari || daftar.saringanAktif.length
                    ? "Tidak ada PD yang cocok dengan pencarian atau saringan ini."
                    : "Belum ada PD."}
                </td></tr>
              )}
              {daftar.tampil.map((d) => (
                <tr key={d.id} className="border-b hover:bg-muted/50" data-testid={`dr-row-${d.id}`}>
                  <td className="px-4 py-2 font-medium">{d.dr_number}</td>
                  {/* ⚠ SENGAJA BUKAN potongan UUID — lebih baik mengaku tidak tahu. */}
                  <td className="px-4 py-2">
                    {d.customer_name || "—"}
                    {d.nama_team && (
                      <div className="text-xs text-muted-foreground" data-testid={`dr-tim-${d.id}`}>Tim: {d.nama_team}</div>
                    )}
                  </td>
                  <td className="px-4 py-2">{labelProduk(d.product_summary_type)}</td>
                  <td className="px-4 py-2">
                    {d.preliminary_qty_atasan}
                    {d.preliminary_qty_celana > 0 && ` + ${d.preliminary_qty_celana}`}
                  </td>
                  <td className="px-4 py-2">{labelPaket(d.package_type)}</td>
                  <td className="px-4 py-2">
                    <DRStatusBadge value={d.dr_status} />
                  </td>
                  <td className="px-4 py-2">
                    <Button
                      data-testid={`dr-open-${d.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/design-requests/${d.id}`)}
                    >
                      Buka
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Paginasi daftar={daftar} testId="dr-paginasi" />
        </CardContent>
      </Card>
    </div>
  );
}
